import { Shield } from "lucide-react";
import ResourcePage from "@/components/ResourcePage";
import { makeAdapter } from "@/lib/supabaseAdapter";

const adapter = makeAdapter({ table: "contacts", filterColumn: "profile_id" });

const RELATIONSHIPS = [
  { value: "Doctor", label: "Doctor / Provider" },
  { value: "Pharmacy", label: "Pharmacy" },
  { value: "Family", label: "Family Member" },
  { value: "School", label: "School / Daycare" },
  { value: "Friend", label: "Friend" },
  { value: "Other", label: "Other" },
];

export default function Contacts() {
  return (
    <ResourcePage
      config={{
        adapter,
        filterBy: "profile_id",
        eyebrow: "People",
        title: "Contacts",
        description: "Doctors, pharmacies, family — and who to call in an emergency.",
        icon: Shield,
        accent: "bg-primary/10 text-primary",
        addLabel: "Add contact",
        testIdPrefix: "contact",
        blank: () => ({
          profile_id: "",
          name: "",
          relationship: "Doctor",
          phone: "",
          email: "",
          address: "",
          notes: "",
          is_emergency: false,
        }),
        transform: (f, { profile }) => ({
          profile_id: f.profile_id,
          profile_name: profile?.name || null,
          name: f.name,
          relationship: f.relationship || null,
          phone: f.phone || null,
          email: f.email || null,
          address: f.address || null,
          notes: f.notes || null,
          is_emergency: !!f.is_emergency,
        }),
        fields: [
          { key: "name", label: "Name", required: true, span: 2, placeholder: "Dr. Patel" },
          {
            key: "relationship",
            label: "Role / relationship",
            type: "select",
            options: RELATIONSHIPS,
            span: 2,
          },
          { key: "phone", label: "Phone", placeholder: "(555) 555-0100" },
          { key: "email", label: "Email", type: "email" },
          { key: "address", label: "Address", type: "textarea" },
          {
            key: "is_emergency",
            label: "Emergency contact?",
            type: "select",
            options: [
              { value: false, label: "No" },
              { value: true, label: "Yes — show on emergency card" },
            ],
            span: 2,
          },
          { key: "notes", label: "Notes", type: "textarea" },
        ],
        render: (c) => (
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-heading text-lg font-bold leading-tight">{c.name}</div>
              {c.is_emergency && (
                <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-destructive">
                  Emergency
                </span>
              )}
            </div>
            <span className="mt-1 inline-block rounded-full bg-secondary/60 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
              {c.relationship || "—"}
            </span>
            {c.phone && (
              <a href={`tel:${c.phone}`} className="mt-3 block text-sm font-medium text-primary hover:underline">
                📞 {c.phone}
              </a>
            )}
            {c.email && (
              <a href={`mailto:${c.email}`} className="mt-1 block text-sm text-muted-foreground hover:text-foreground">
                ✉ {c.email}
              </a>
            )}
            {c.address && (
              <div className="mt-2 text-xs text-muted-foreground">{c.address}</div>
            )}
            {c.notes && (
              <div className="mt-2 text-xs text-muted-foreground">{c.notes}</div>
            )}
          </div>
        ),
      }}
    />
  );
}
